import type { TracePog } from '../../archon/services/MatchTraceApiService';
import {
  ForgeApiError,
  forgeBlobRequest,
  forgeRequest,
  forgeVoidRequest,
} from '../../forge/services/forgeRequest';
import type { BarcodeStatus } from '../../forge/types/designs';

const API_DL_URL = process.env.REACT_APP_API_DL_URL;

if (!API_DL_URL) {
  throw new Error('REACT_APP_API_DL_URL is required for Lurker API requests');
}

export const LURKER_API_URL = `${API_DL_URL}/v1/forge/lurker`;

/**
 * An algorithm whose results Lurker compares. core: what stores are served. pr: the pr_ twins the PR
 * pipeline writes in shadow mode. pc_shadow: planogram compliance's verdict per template box.
 * pc_baseline: the products planogram compliance matched (final UPC). pr_baseline: product
 * recognition's top-1 product per detection.
 */
export type OosSource = 'core' | 'pr' | 'pc_shadow' | 'pc_baseline' | 'pr_baseline';
export const OOS_SOURCES: OosSource[] = ['core', 'pr', 'pc_shadow', 'pc_baseline', 'pr_baseline'];

/** A stock state in label_items spelling, as algorithms serve it. */
export type OosCellState = 'in' | 'low' | 'out' | 'face_over' | 'n/a';
/**
 * A task a label accepts, as the stock state that calls for it: in is no task. There is no N/A:
 * where the slot cannot be seen, the item keeps its task.
 */
export type OosLabelState = Exclude<OosCellState, 'n/a'>;
export type OosPairing = 'correct' | 'wrong' | 'box' | 'unsure';

export interface OosStrategyFields {
  name: string;
  source: OosSource;
  /** Mod processing types whose planogram items count; empty for all. */
  processingTypes: string[];
  storeIds: number[];
  fromDate: string | null;
  toDate: string | null;
  /** Holds the camera's tasks at obstructed captures; null keeps every answer. */
  obstruction: OosObstructionRule | null;
}

/**
 * What an obstruction rule measures at each capture. person: the share of the camera's mods person
 * detection covers. any: the largest share of one mod any visual obstruction covers.
 */
export type OosObstruction = 'person' | 'any';

/**
 * At a capture whose measured share exceeds `threshold` (0 to 1), the algorithm's answers are
 * dropped once it has run on the item that day, captures it skipped included, so the tasks stay
 * as the previous captures left them.
 */
export interface OosObstructionRule {
  kind: OosObstruction;
  threshold: number;
}

export interface OosStrategy extends OosStrategyFields {
  id: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

export interface OosCapture {
  imageId: number;
  capturedAt: string;
  imageUri: string | null;
  /** The share of the camera's mods person detection covers, 0 to 1; null when unknown. */
  modPersonArea: number | null;
  /**
   * The largest share of one mod PR's visual obstructions cover (people masked out, misplaced
   * items), as PC's obstruction gate measures it, 0 to 1; null without a PR inference.
   */
  obstructionArea: number | null;
}

export interface OosPlanogramItem {
  ilItemId: number;
  productId: number | null;
  upc: string | null;
  itemNumber: string | null;
  productName: string | null;
  modCoverageId: number | null;
  processingType: string | null;
  pogName: string | null;
  modNumber: number | null;
  shelfNumber: number | null;
  positionNumber: number | null;
  facings: number | null;
  /** The item's department code (mod prefix) in Forge, such as DA; null when unknown. */
  modPrefix: string | null;
  /** The product's first reference image for the chain, as a CDN URL. */
  productImageUri: string | null;
}

export interface OosRecord {
  source: OosSource;
  oosId: number;
  productId: number | null;
  locationKey: string | null;
  state: string | null;
  stage: string | null;
  isActive: boolean;
  startTime: string | null;
  endTime: string | null;
}

/**
 * Core's model output for one box at one capture (an inference_oos row). Post probabilities follow
 * the known-NA rule, which drops N/A and rescales the rest when p(N/A) is under the runner's
 * threshold. A propagated box has no raw probabilities: it copies the previous capture's post and
 * final values when the two crops' 64-bit hashes differ in few bits (similarityScore).
 */
export interface OosInference {
  inferenceId: number;
  modelId: number | null;
  inferredAt: string;
  predClass: string | null;
  inProb: number | null;
  lowProb: number | null;
  outProb: number | null;
  naProb: number | null;
  predClassPost: string | null;
  inProbPost: number | null;
  lowProbPost: number | null;
  outProbPost: number | null;
  naProbPost: number | null;
  predClassFinal: string | null;
  predProbFinal: number | null;
  confidenceLevel: number | null;
  isKnownNa: boolean;
  prevImageId: number | null;
  /** Differing bits between this crop's hash and the previous capture's; 0 is identical. */
  similarityScore: number | null;
  source: string | null;
  reason: string | null;
}

/**
 * A change PC's decision adjustments made to a slot's verdict (mod_compliance_items.
 * decision_adjustment): the category, such as out_forgiveness, and its reason.
 */
export interface OosPcAdjustment {
  category: string;
  reason: string;
}

/**
 * The OOS model's output on the PR template box behind a Shadow PC box: its top class and the four
 * probabilities, 0 to 1.
 */
export interface OosModelPrediction {
  predictedClass: string;
  inProb: number | null;
  lowProb: number | null;
  outProb: number | null;
  naProb: number | null;
}

/** A polygon vertex in image pixels. */
export interface OosPoint {
  x: number;
  y: number;
}

/**
 * One box of a result. Slot sources (core, pr, pc_shadow) box a planogram slot (ilItemId) and serve
 * a stock state for it. Presence sources (pc_baseline, pr_baseline) box a detected product
 * (productId, itemNumber) with state 'in'.
 */
export interface OosBox {
  bboxId: number;
  ilItemId: number | null;
  productId: number | null;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  angle: number | null;
  mod: number | null;
  shelf: number | null;
  column: number | null;
  /**
   * The box's set is_* flags. Only the runner's four active ones (KNA_FLAGS in lib/cameraDay) make
   * it a known-NA box; is_spill and is_partially_obstructed do not.
   */
  flags: string[];
  state: string;
  reason: string | null;
  itemSource: string | null;
  probPct: number | null;
  oosId: number | null;
  /** Core only; null when the snapshot found no inference_oos row, which MySQL keeps for days. */
  inference: OosInference | null;
  /** The detection's uuid: PR polygon, or PC's rog_uuid. */
  uuid: string | null;
  /** The barcode a detection was identified as: PR top-1, or PC's final UPC. */
  upc: string | null;
  /** The item number of the product a detection was identified as. */
  itemNumber: string | null;
  /**
   * Top-1 similarity of the detections behind the box, 0 to 1. PR baseline: its top-1 candidate's.
   * PC baseline: that of the PR detection PC matched (rog_uuid). Shadow PC: the highest over the
   * slot's matched detections. Null when the snapshot had no matching PR inference (Mongo keeps
   * them for 3 days).
   */
  similarity: number | null;
  /** PC baseline: the planogram slot PC matched the detection to. */
  slotIlItemId: number | null;
  /** PR baseline: the detection's polygon, which x1..y2 bound; null for a rectangle. */
  points: OosPoint[] | null;
  /**
   * Shadow PC: the distinct decision adjustments of the compliance items PC matched to the slot.
   * Empty both when PC made none and when the snapshot predates the read (`prInferenceRead` null).
   */
  adjustments?: OosPcAdjustment[];
  /**
   * Shadow PC: the OOS model's output on the template box behind PC's box. Null when the template
   * box was occupied, no PR template box overlaps PC's box, or the PR inference was not read
   * (`prInferenceRead` says which).
   */
  oosPrediction?: OosModelPrediction | null;
  /** Shadow PC: PR saw a product in the template box, so the OOS model did not run on it. */
  templateBoxOccupied?: boolean | null;
  /**
   * Shadow PC: true when the snapshot read the capture's PR inference, false when it could not
   * (Mongo keeps them for 3 days). Null for other sources and for boxes snapshotted before Lurker
   * read PC's OOS model and adjustments.
   */
  prInferenceRead?: boolean | null;
}

export interface OosCaptureResult {
  source: OosSource;
  imageId: number;
  /** The label record; null for the sources that write none. */
  recordId: number | null;
  templateId: number | null;
  templateCreatedAt: string | null;
  servedAt: string;
  boxes: OosBox[];
  /** The run the result came from: PR inference id, or PC's inference_object_id. */
  runId: string | null;
  /** PC baseline: the planogram items PC evaluated in the capture. */
  coveredIlItemIds: number[];
}

export interface OosCellLabel {
  /** The row: p:<productId>, or il:<ilItemId> for an item without a product or split into rows. */
  itemKey: string;
  imageId: number;
  states: OosLabelState[];
  labelledBy: string;
  labelledAt: string;
}

export interface OosPairingLabel {
  source: OosSource;
  segmentKey: string;
  pairing: OosPairing;
  labelledBy: string;
  labelledAt: string;
}

interface OosCameraDayHeader {
  id: string;
  cameraId: number;
  date: string;
  storeId: number;
  storeName: string;
  chainId: number;
  sources: OosSource[];
  /** When the camera-day was first snapshotted; a re-snapshot keeps it. */
  firstSnapshotAt: string;
  snapshotAt: string;
  /** The snapshot and scoring version of the stored score; null when never scored. */
  scoredSnapshotAt: string | null;
  scoredVersion: number | null;
  /** Bumped by every label write; a score computed from an older revision is stale. */
  labelsRevision: number;
  scoredLabelsRevision: number | null;
  /** Lowercase tags a person gave the camera-day; a re-snapshot keeps them. */
  tags: string[];
}

export interface OosCameraDaySummary extends OosCameraDayHeader {
  captureCount: number;
  itemCount: number;
  labelledCells: number;
  labelledPairings: number;
}

export interface OosCameraDay extends OosCameraDayHeader {
  /** The store's name in Control (stores.store_name), read live; null when the camera is gone. */
  controlStoreName: string | null;
  /** The camera's device name in Control, read live; null when the camera is gone. */
  deviceName: string | null;
  timeZone: string | null;
  /** The store opens an OOS record on LOW as well as OUT. */
  generateOutsFromLows: boolean;
  captures: OosCapture[];
  items: OosPlanogramItem[];
  records: OosRecord[];
  results: OosCaptureResult[];
  cellLabels: OosCellLabel[];
  pairingLabels: OosPairingLabel[];
}

export interface OosSourceCaptures {
  source: OosSource;
  /** Captures of the camera-day with the source's results. */
  captures: number;
}

export interface OosCandidate {
  cameraId: number;
  date: string;
  storeId: number;
  storeName: string;
  chainId: number;
  /** The captures the source served; for core and several sources, every capture of the camera-day. */
  captures: number;
  /** The chosen sources' captures with results. */
  sources: OosSourceCaptures[];
  /** When the source last served the camera-day; null when several sources are listed. */
  lastServedAt: string | null;
  snapshotAt: string | null;
}

/** Whether a candidate needs every chosen source or any of them. */
export type OosCandidateMatch = 'all' | 'any';

/**
 * What an algorithm said about a planogram item at a capture: a stock state (state algorithms),
 * found / not_found (presence algorithms), none (it served the capture but gave the item no verdict)
 * or not_run (no result at the capture, or the item was outside what it evaluated).
 */
export type OosScoreValue = OosCellState | 'found' | 'not_found' | 'none' | 'not_run';

/** A task by capture index: opened at `openIdx`, closed at `closeIdx` (null: open at day end). */
export interface OosTaskRecord {
  openIdx: number;
  closeIdx: number | null;
}

/** One algorithm on one item through the day. */
export interface OosSourceItemScore {
  source: OosSource;
  /** One per capture. */
  values: OosScoreValue[];
  /** One per capture for slot sources: the pairing verdict of the box positions; empty otherwise. */
  pairing: (OosPairing | null)[];
  records: OosTaskRecord[];
  pairingChanges: number;
}

/** One labelled planogram item (row) through the day. */
export interface OosItemScore {
  itemKey: string;
  ilItemId: number;
  productId: number | null;
  itemNumber: string | null;
  processingType: string | null;
  modPrefix: string | null;
  /** The accepted tasks per capture; null where the capture is not labelled. */
  truth: (OosLabelState[] | null)[];
  sources: OosSourceItemScore[];
}

/** A labelled camera-day: the labels and what every algorithm said, before any strategy filters. */
export interface OosScore {
  imageIds: number[];
  generateOutsFromLows: boolean;
  items: OosItemScore[];
  /** One per capture: OosCapture.modPersonArea. Empty in older scores. */
  modPersonAreas: (number | null)[];
  /** One per capture: OosCapture.obstructionArea. Empty in older scores. */
  obstructionAreas: (number | null)[];
}

export interface OosCameraDayScore {
  cameraDayId: string;
  cameraId: number;
  date: string;
  storeId: number;
  storeName: string;
  chainId: number;
  scoringVersion: number;
  snapshotAt: string;
  labelsRevision: number;
  stale: boolean;
  computedAt: string;
  /** The camera-day's current tags; a tag change does not make the score stale. */
  tags: string[];
  score: OosScore;
  /**
   * One per entry of `score.imageIds`: the capture's time, null when the image is not among the
   * camera-day's captures. Absent from older backends.
   */
  captureTimes?: (string | null)[];
  /** The store's time zone. Absent from older backends. */
  timeZone?: string;
}

export interface OosDateRange {
  firstDate: string;
  lastDate: string;
}

/** Whether a camera-day needs any of the picked tags or all of them. */
export type OosTagMatch = 'any' | 'all';

export interface OosCameraDayFilter {
  /** No tags lists camera-days with or without tags. */
  tags?: string[];
  tagMatch?: OosTagMatch;
}

/** A tag and how many of the camera-days the user can see carry it. */
export interface OosTagCount {
  tag: string;
  cameraDays: number;
}

/** A delete's camera-days, and the labels deleted with them. */
export interface OosCameraDaysDeleted {
  deleted: string[];
  cellLabels: number;
  pairingLabels: number;
}

/** One design of an item and its image: the front photo, else its active representative crop. */
export interface OosDesignImage {
  designId: string;
  normalizedBarcode: string;
  barcodeStatus: BarcodeStatus;
  imageUrl: string | null;
  imageKind: 'front' | 'representative' | null;
}

/** A recent front embedding crop of one of an item's barcodes. */
export interface OosCropImage {
  cropId: string;
  normalizedBarcode: string;
  imageUrl: string;
}

/** The Forge designs of one item of a camera-day, matched by item number, else by UPC. */
export interface OosItemImages {
  /** The camera-day's planogram items with this item number (or UPC). */
  ilItemIds: number[];
  itemNumber: string | null;
  /** The barcodes searched, at most 20. */
  barcodes: string[];
  /**
   * Whether the chain's catalog stores list the item number, adding their barcodes. False for an
   * item without an item number, a chain without a catalog store, or an item number the catalog
   * does not list.
   */
  catalogResolved: boolean;
  /** Every design found; `designs` lists at most 6 of them. */
  designCount: number;
  /** Oldest first, at most 6. */
  designs: OosDesignImage[];
  /**
   * Random front crops of the item's barcodes, other than the representative crops in `designs`,
   * topping the design images up to 3.
   */
  crops: OosCropImage[];
}

export interface OosCellLabelInput {
  itemKey: string;
  imageId: number;
  /** No states clears the cell. */
  states: OosLabelState[];
}

export interface OosPairingLabelInput {
  source: OosSource;
  segmentKey: string;
  /** null clears the segment. */
  pairing: OosPairing | null;
}

/** A label write's revision, and the one before it: another writer when it is not the caller's. */
export interface OosLabelsSaved {
  revision: number;
  previous: number;
}

/** A named reason a Strategy's answer was wrong, from a list the team grows. */
export interface OosErrorBucket {
  id: string;
  name: string;
  createdBy: string;
  createdAt: string;
}

/** The bucket a person put one Strategy's wrong answer for an item at a capture in. */
export interface OosBucketAssignment {
  cameraDayId: string;
  itemKey: string;
  imageId: number;
  strategyId: string;
  bucketId: string;
  note: string | null;
  assignedBy: string;
  assignedAt: string;
}

export interface OosBucketAssignmentInput {
  itemKey: string;
  imageId: number;
  strategyId: string;
  /** null clears the assignment. */
  bucketId: string | null;
  /** Sent only by the note editor: left out, the stored note stays; null clears it. */
  note?: string | null;
}

/** The camera-days whose assignments to read: these ids, else the list a scores read would give. */
export interface OosBucketAssignmentQuery {
  cameraDayIds?: string[];
  range?: OosDateRange;
  filter?: OosCameraDayFilter;
}

/** Lurker settings everyone shares: the mod prefixes the Dashboard leaves out. */
export interface OosLurkerSettings {
  excludedModPrefixes: string[];
  updatedBy: string | null;
  updatedAt: string | null;
}

/** `saveSettings` refused: someone saved the settings after they were loaded. */
export const isSettingsConflict = (error: unknown) =>
  error instanceof ForgeApiError && error.status === 409;

const rangeQuery = ({ firstDate, lastDate }: OosDateRange) =>
  new URLSearchParams({ first_date: firstDate, last_date: lastDate }).toString();

// A camera-day list filtered by tags, and by dates only when a range is given.
const listUrl = (
  path: string,
  range?: OosDateRange,
  { tags = [], tagMatch }: OosCameraDayFilter = {}
) => {
  const query = new URLSearchParams(range && rangeQuery(range));
  for (const tag of tags) query.append('tags', tag);
  if (tags.length && tagMatch) query.set('tag_match', tagMatch);
  const params = query.toString();
  return `${LURKER_API_URL}/${path}${params ? `?${params}` : ''}`;
};

/** Which boxes a batch draws: false detections, or any the model predicted. */
export type OosDrawnBatchKind = 'false_detections' | 'prod_sample';
/** A drawn batch, or the crops of old label tables imported with each table's labels. */
export type OosBatchKind = OosDrawnBatchKind | 'imported';
/** Where a batch reads its boxes: BigQuery adds the model's four probabilities and the mod. */
export type OosDrawnBatchSource = 'mysql' | 'bigquery';
/** A drawn batch's source, or the old label tables an imported batch came from. */
export type OosBatchSource = OosDrawnBatchSource | 'tables';
/** A crop's OOS state as a labeller sees it; `unsure` stays out of training tables. */
export type OosCropLabel = 'in' | 'low' | 'out' | 'n/a' | 'unsure';

/** A state the store was served for a crop. */
export type OosServedState = 'in' | 'low' | 'out' | 'n/a';

/**
 * Keeps crops served this state with a served probability from `minProbPct` to `maxProbPct`,
 * both included, as whole percents. A crop without a probability passes only 0 to 100.
 */
export interface OosServedFilter {
  state: OosServedState;
  minProbPct: number;
  maxProbPct: number;
}

/** Keeps boxes whose model probability of `state` is within the range; every entry must hold. */
export type OosClassFilter = OosServedFilter;

/** The model's probability of each state, as a fraction. */
export type OosClassProbs = Record<OosServedState, number>;

export interface OosBatchRequest {
  name: string;
  kind: OosDrawnBatchKind;
  source: OosDrawnBatchSource;
  /** 1 to 10 stores of one chain; empty when `chainId` asks for the whole chain. */
  storeIds: number[];
  /** Every store of the chain but `excludedStoreIds`, for at most 2 days. */
  chainId?: number | null;
  /** Stores of `chainId` left out; empty without a chain. */
  excludedStoreIds: number[];
  /** Leaves out boxes labelled in any batch. */
  skipLabelled: boolean;
  firstDate: string;
  lastDate: string;
  size: number;
  /** At most one entry per state; empty keeps every crop. */
  servedFilter: OosServedFilter[];
  /** BigQuery only: at most one entry per state. */
  classFilter?: OosClassFilter[];
  /** BigQuery only: mod tag prefixes; empty keeps every mod. */
  modPrefixes?: string[];
  /** People who label each crop blind, 1 to MAX_VOTES_NEEDED; 1 makes no voting batch. */
  votesNeeded?: number;
  consensusRule?: OosConsensusRule;
}

/** Either the stores, or a whole chain with the stores it leaves out. */
export type OosBatchAvailabilityRequest = {
  kind: OosDrawnBatchKind;
  source: OosDrawnBatchSource;
  firstDate: string;
  lastDate: string;
  /** The filters below count, and are sent, only for BigQuery. */
  servedFilter?: OosServedFilter[];
  classFilter?: OosClassFilter[];
  modPrefixes?: string[];
} & ({ storeIds: number[] } | { chainId: number; excludedStoreIds: number[] });

/**
 * Crops each store holds for a batch's kind and dates, in request order. A whole chain lists only
 * the stores holding some.
 */
export interface OosBatchAvailability {
  stores: { storeId: number; available: number }[];
}

/** Crops of one store and kind already in batches, summed over the batches the user can see. */
export interface OosBatchCollected {
  storeId: number;
  kind: OosDrawnBatchKind;
  crops: number;
  labelled: number;
}

/** A batch held out of training as a val or a test set; its labels are gold answers. */
export type OosHoldout = 'val' | 'test';

/** What the signed-in user may do in Lurker. */
export interface LurkerAccess {
  /** A Lurker owner sees the holdout splits and alone labels their batches' crops. */
  owner: boolean;
  /** A reviewer settles relabel tasks and, like an owner, sees votes. */
  reviewer?: boolean;
  /** Every reviewer's email, the people a relabel task may be assigned to. */
  reviewers?: string[];
}

/** The most people who may label one crop of a voting batch (the server's MAX_VOTES_NEEDED). */
export const MAX_VOTES_NEEDED = 3;

/** When the votes on a crop make its final label: every vote, or more than half, agrees. */
export type OosConsensusRule = 'all' | 'majority';

/** Open crops take votes; needs review: the votes needed are in and disagree. */
export type OosVoteState = 'open' | 'final' | 'needs_review';

/** How a voting crop got its final label; inherited: the label it had when voting was turned on. */
export type OosSettlement = 'consensus' | 'inherited' | 'review' | 'override';

/** A voting batch's setting. */
export interface OosVoting {
  votesNeeded: number;
  rule: OosConsensusRule;
  setBy: string;
  setAt: string;
}

/** One person's label set for a crop; `by` is `import` for an inherited label no one in Lurker gave. */
export interface OosCropVote {
  by: string;
  labels: OosCropLabel[];
  at: string;
  inherited?: boolean;
}

/** How many crops two voters (`a` before `b` by email) gave one pair of label sets. */
export interface OosVotePair {
  a: string;
  b: string;
  aLabels: OosCropLabel[];
  bLabels: OosCropLabel[];
  crops: number;
}

/** What a voting change did, or would do on a dry run. */
export interface OosVotingChanged {
  batch: OosBatch;
  voteStateCounts: Partial<Record<OosVoteState, number>>;
  /** Crops whose final label or state changes. */
  changed: number;
  /** Final crops whose label clears because they need more votes. */
  reopened: number;
}

/** Crops of one voting batch that need review, assigned to one reviewer, with their progress. */
export interface RelabelTask {
  id: string;
  chainId: number;
  name: string;
  batchId: string;
  assignee: string;
  createdBy: string;
  createdAt: string;
  ready: number;
  done: number;
}

/** A new relabel task (null when no crop was taken) and what happened to each crop. */
export interface RelabelTaskSaved {
  task: RelabelTask | null;
  saved: number;
  merged: number;
  reopened: number;
  inOtherTask: number;
  notNeedingReview: number;
  unknown: number;
  otherChain: number;
  conflict: number;
}

/** An answer's confusion matrix column: its one label, several labels, or unsure. */
export type QaAnswer = 'in' | 'low' | 'n/a' | 'out' | 'several' | 'unsure';
export type QaSort = 'recent' | 'disputed';

/** Answers on gold crops carrying one class, and how many were right. */
export interface QaClassRecall {
  label: OosCropLabel;
  answered: number;
  right: number;
}

/** One labeller's answers on gold crops, or everyone's when `labeller` is null. */
export interface QaScore {
  labeller: string | null;
  answered: number;
  right: number;
  accuracy: number | null;
  /** The accuracy's Wilson 95% interval. */
  accuracyLow: number | null;
  accuracyHigh: number | null;
  /** Per gold class, n/a first; a crop counts for every class it carries. */
  recall: QaClassRecall[];
  unsure: number;
  unsureShare: number | null;
  /** Answers given against gold labels that differ from today's. */
  goldChanged: number;
  /** Answers wrong when given and right since a gold correction. */
  goldFixes: number;
  lastAt: string | null;
}

/** Answers with gold labels `gold` (e.g. `in|n/a`) in one answer column. */
export interface QaMatrixCell {
  gold: string;
  answer: QaAnswer;
  answers: number;
  wrong: number;
}

/** Labellers' accuracy on gold crops, scored against the gold labels now. */
export interface QaSummary {
  chainId: number | null;
  firstDate: string;
  lastDate: string;
  labellerFilter: string[];
  /** Everyone with an answer in the chain and dates, for the labeller filter. */
  chainLabellers: string[];
  /** Everyone's scores, whatever the labeller filter; the matrix covers the filtered labellers. */
  total: QaScore;
  labellers: QaScore[];
  matrix: QaMatrixCell[];
}

/** A labeller's answer on a gold crop, with the crop as its holdout batch holds it now. */
export interface QaCheck {
  labeller: string;
  batchId: string;
  cropId: string;
  holdoutBatchId: string;
  labels: OosCropLabel[];
  goldLabels: OosCropLabel[];
  /** The gold labels when the labeller answered. */
  goldLabelsThen: OosCropLabel[];
  goldChanged: boolean;
  right: boolean;
  gold: string;
  answer: QaAnswer;
  at: string;
  imageUri: string | null;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  productName: string | null;
  storeName: string | null;
  capturedAt: string | null;
  /** Everyone's answers on the crop in the chain and dates, and the wrong ones. */
  cropAnswers: number;
  cropWrong: number;
}

/** The chain (or every chain), UTC dates and labellers QA scores, and how it scores them. */
export interface QaFilter {
  chainId: number | 'all';
  firstDate?: string;
  lastDate?: string;
  labellers: string[];
  /** Right only with exactly the gold labels: In is wrong on an In + Low crop. */
  exact?: boolean;
  /** Unsure answers count as wrong; otherwise they score nothing. */
  unsureWrong?: boolean;
}

/** Narrows the wrong answers to one matrix cell, and orders them. */
export interface QaChecksFilter extends QaFilter {
  gold?: string | null;
  answer?: QaAnswer | null;
  sort: QaSort;
}

/** What a split holds out: whole stores of the chain, or whole weeks of every store. */
export type OosHoldoutUnit = 'stores' | 'weeks';

/** Store-local capture dates, both inclusive. */
export interface OosHoldoutWindow {
  firstDate: string;
  lastDate: string;
}

/** One held-out set of a split: its stores or its windows, and the batches that score models on it. */
export interface OosHoldoutSet {
  stores: number[];
  windows: OosHoldoutWindow[];
  batchIds: string[];
}

/** A chain's holdout split as dl-oos2 last pushed it, with its batches that still exist. */
export interface OosHoldoutSplit {
  name: string;
  chainId: number;
  unit: OosHoldoutUnit;
  /** The draw that assigned stores or weeks to val and test; null when picked by hand. */
  seed: number | null;
  /** Weeks only: days on each side of a window that training also leaves out. */
  bufferDays: number;
  val: OosHoldoutSet;
  test: OosHoldoutSet;
  pushedBy: string;
  pushedAt: string;
  batches: OosBatch[];
}

/**
 * A batch assigned to the caller, with their progress: the crops anyone labelled and the gold crops
 * they answered, of the batch's crops and their gold crops.
 */
export interface OosWorkBatch {
  id: string;
  name: string;
  labelled: number;
  crops: number;
  /** Voting batches: how many people label each crop blind. */
  votesNeeded?: number | null;
}

/** A crop as a labeller sees it: the photo with its box, the product and their own labels. */
export interface OosWorkCrop {
  cropId: string;
  imageUri: string | null;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  productName: string | null;
  labels: OosCropLabel[];
  /**
   * The label to expect mostly where the crop sits: the predicted class the view is sorted by, null
   * among the crops without one. A gold crop carries that of the crop after it, never its own.
   */
  expectedLabel: OosCropLabel | null;
}

export interface OosBatch {
  id: string;
  name: string;
  kind: OosBatchKind;
  source: OosBatchSource;
  chainId: number;
  chainName: string;
  storeIds: number[];
  firstDate: string;
  lastDate: string;
  /** Crops asked for; `cropCount` is what the stores and dates held. */
  size: number;
  cropCount: number;
  createdBy: string;
  createdAt: string;
  /** Crops carrying each label, a crop once per label; `multi` counts crops with two or more. */
  labelCounts: Partial<Record<OosCropLabel | 'multi', number>>;
  /** Crops carrying any label, each once. */
  labelledCount: number;
  /** The served filter the batch drew with; empty, or missing on older batches, kept every crop. */
  servedFilter?: OosServedFilter[];
  /** Drawn from a whole chain; `storeIds` then lists the stores its crops came from. */
  wholeChain?: boolean;
  /** Stores the whole chain left out. */
  excludedStoreIds?: number[];
  /** Left out boxes already labelled in a batch. */
  skipLabelled?: boolean;
  /** The model-probability filter and mod prefixes a BigQuery batch drew with. */
  classFilter?: OosClassFilter[];
  modPrefixes?: string[];
  /** The old label tables an imported batch came from. */
  sourceTables?: string[];
  /** The labellers the batch is assigned to, by email. */
  assignees: string[];
  /** Labelled crops by the email of whoever labelled each last. */
  labelledBy: Record<string, number>;
  /** Set on a val or test batch of its chain's holdout split, whose crops new batches never draw. */
  holdout?: OosHoldout | null;
  /** Who last pushed a split that changed the batch's holdout, and when. */
  holdoutSetBy?: string | null;
  holdoutSetAt?: string | null;
  /** Set on a voting batch, whose crops each need several people's blind votes. */
  voting?: OosVoting | null;
  /** Voting batches: crops by vote state, and votes by voter. */
  voteStateCounts?: Partial<Record<OosVoteState, number>>;
  votesBy?: Record<string, number>;
}

/** One old label table's labels for a crop; the table's "uncertain" reads unsure. */
export interface OosTableLabels {
  table: string;
  labels: OosCropLabel[];
}

/** One box on a production capture, frozen with what the store was served for it. */
export interface OosBatchCrop {
  cropId: string;
  imageId: number;
  bboxId: number;
  cameraId: number;
  storeId: number;
  storeName: string;
  capturedAt: string | null;
  imageUri: string | null;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  productId: number | null;
  upc: string | null;
  itemNumber: string | null;
  productName: string | null;
  servedState: string | null;
  servedReason: string | null;
  /** The served class's probability, in percent. */
  servedProbPct: number | null;
  oosId: number | null;
  /** The model's four probabilities, for BigQuery crops only. */
  classProbs: OosClassProbs | null;
  /** When a store worker closed the record's task as a false detection. */
  actionedAt: string | null;
  /** The crop's labels in in, low, n/a, out order, or unsure alone; empty when unlabelled. */
  labels: OosCropLabel[];
  /** The one label of a crop with exactly one, else null; kept for older readers. */
  label: OosCropLabel | null;
  labelledBy: string | null;
  labelledAt: string | null;
  /** The crop's label writes in Lurker across its chain's batches, oldest first. */
  history: OosCropLabelChange[];
  /** Imported crops only: each old table's labels; empty for drawn crops. */
  tableLabels: OosTableLabels[];
  /** The old tables disagreed and no rule settled it; the next label write clears it. */
  needsReview: boolean;
  /** The crop's relabel candidate, or null when it never was one. */
  relabel: CropRelabelFacts | null;
  /** Relabel queue rows only: the first copy's batch, for the crop's details. */
  batchId?: string;
  /** Relabel queue rows only: every batch copy's labels, by batch id. */
  copyLabels?: RelabelCopyLabels[];
  /** Voting batches: the votes, for owners and reviewers only, and where the crop stands. */
  votes?: OosCropVote[];
  voteState?: OosVoteState | null;
  settlement?: OosSettlement | null;
}

/** One label write of a crop in Lurker; no labels is a clear, and a confirm kept the labels. */
export interface OosCropLabelChange {
  labels: OosCropLabel[];
  by: string;
  at: string;
  kind?: OosLabelEventKind | null;
}

/** A label write, a relabel's confirm, a voter's vote, or a voting crop's final label set by its votes. */
export type OosLabelEventKind = 'label' | 'confirm' | 'vote' | 'consensus';

/** An uploaded model's class probabilities (fractions) by crop id; not every crop has them. */
export interface OosModelScores {
  model: string;
  probs: Record<string, OosClassProbs>;
}

/** A batch holding a crop, with the crop's labels there. */
export interface OosCropBatchRef {
  id: string;
  name: string;
  kind: OosBatchKind;
  labels: OosCropLabel[];
  needsReview: boolean;
}

/**
 * One entry of a crop's label history: an old table's labels (`table` set, `at` the import time),
 * or a label write in Lurker (`table` null; no labels is a clear).
 */
export interface OosCropLabelEvent {
  batchId: string;
  labels: OosCropLabel[];
  by: string;
  at: string;
  table: string | null;
  /** The labels before the write; null when they were mixed or are unknown (older events). */
  prev?: OosCropLabel[] | null;
  /** A confirm keeps the labels a relabel agreed with; null on older events is a label. */
  kind?: OosLabelEventKind | null;
  /** The relabel runs that nominated the crop when it was written. */
  runIds?: string[] | null;
  /** Every batch copy a relabel write reached, whose first is `batchId`; null on batch-page writes. */
  batchIds?: string[] | null;
  /** Voting batches: the crop's vote state after the write. */
  state?: OosVoteState | null;
  /** A relabel write of a relabel task. */
  taskId?: string | null;
}

export type OosTrainingRole = 'train' | 'val' | 'test';

/** A training run's use of a crop: its role and the tables of the data snapshot that held it. */
export interface OosCropUsage {
  model: string;
  snapshot: string;
  role: OosTrainingRole;
  tables: string[];
}

/** Everything known about one crop across the batches of its chain; history oldest first. */
export interface OosCropDetail {
  cropId: string;
  batches: OosCropBatchRef[];
  history: OosCropLabelEvent[];
  usage: OosCropUsage[];
  relabel?: CropRelabelFacts | null;
}

/** Narrows the batch list; without either, every batch the user can see. */
export interface OosBatchListFilter {
  kinds?: OosBatchKind[];
  chainId?: number;
}

/** One crop's new labels; empty clears them. */
export interface OosCropLabelInput {
  cropId: string;
  labels: OosCropLabel[];
}

/** Why a crop was sent to relabel: a flag model's strict or loose pick, a rule, an audit draw, or a person. */
export type RelabelRule =
  | 'cl'
  | 'loose'
  | 'unsure'
  | 'multi_label'
  | 'audit'
  | 'manual'
  | 'votes_disagree';
export type RelabelStatus = 'ready' | 'done' | 'dismissed';
/** The statuses the relabel queue lists; dismissed crops never show. */
export type RelabelQueueStatus = 'ready' | 'done';
/** confirmed: the relabel kept the labels. reverted: a later relabel put the earlier labels back. */
export type RelabelOutcome = 'confirmed' | 'changed' | 'unsure' | 'reverted';

export interface RelabelReason {
  rule: RelabelRule;
  runId: string | null;
  /** 0 to 1; null for a test store's crop. */
  score: number | null;
  /** The flag models' top class; null for a test store's crop. */
  suggested: OosCropLabel[] | null;
  by: string | null;
  at: string;
}

/** A crop's relabel candidate as batch reads, crop details and the relabel queue see it. */
export interface CropRelabelFacts {
  status: RelabelStatus;
  outcome: RelabelOutcome | null;
  /** The store's split in the run that nominated the crop; test never changes. */
  role: OosTrainingRole | null;
  rules: RelabelRule[];
  runIds: string[];
  resolvedRules: RelabelRule[];
  resolvedRunIds: string[];
  /** Labels at the first nomination; null when the copies disagreed. */
  labelsFirst: OosCropLabel[] | null;
  labelsAtNomination: OosCropLabel[] | null;
  changes: number;
  confirmations: number;
  resolvedAt: string | null;
  reasons: RelabelReason[];
}

/** One batch copy of a queued crop and its labels there. */
export interface RelabelCopyLabels {
  batchId: string;
  labels: OosCropLabel[];
}

/** Reason and role keys of the relabel summary, camelCased as the server sends them. */
export type RelabelReasonKey =
  | 'manual'
  | 'cl'
  | 'loose'
  | 'multiLabel'
  | 'unsure'
  | 'audit'
  | 'votesDisagree';
export type RelabelRoleKey = OosTrainingRole | 'none';
export type RelabelOutcomeCounts = Record<RelabelOutcome, number>;

/** A chain's relabel queue, what its relabels decided and its runs. */
export interface RelabelSummary {
  chainId: number;
  statusCounts: Record<RelabelStatus, number>;
  /** Ready crops per reason, a crop once per reason it carries. */
  readyByReason: Record<RelabelReasonKey, number>;
  readyByRole: Record<RelabelRoleKey, number>;
  outcomeCounts: RelabelOutcomeCounts;
  outcomeByReason: Record<RelabelReasonKey, RelabelOutcomeCounts>;
  outcomeByRole: Record<RelabelRoleKey, RelabelOutcomeCounts>;
  /** Newest first, at most 20. */
  runs: {
    runId: string;
    rule: string;
    createdAt: string;
    complete: boolean;
    counts: Record<string, number>;
  }[];
}

/** Crops carrying each label, a crop once per label; `multi` counts crops with two or more. */
export interface LabelMix {
  in: number;
  low: number;
  out: number;
  na: number;
  unsure: number;
  multi: number;
}

/** What the labelling summary counts: one chain or every accessible one, over UTC dates. */
export interface LabellingQuery {
  chainId: number | 'all';
  firstDate: string;
  lastDate: string;
  /** Exact emails; empty counts everyone. */
  labellers: string[];
  kind: OosBatchKind | null;
  batchId: string | null;
  includeImported: boolean;
}

export interface LabellerActivity {
  labeller: string;
  crops: number;
  /** The labels the labeller last gave each crop. */
  labels: LabelMix;
  labelWrites: number;
  confirmations: number;
  /** Writes that replaced different, non-empty labels, whoever had given them. */
  changes: number;
  firstAt: string;
  lastAt: string;
}

/** Crops labelled, per labeller and UTC day, store, batch and kind, and the relabel outcomes. */
export interface LabellingSummary {
  chainId: number | null;
  firstDate: string;
  lastDate: string;
  kinds: OosBatchKind[];
  batchId: string | null;
  labellerFilter: string[];
  /** The first label event in scope: writes, confirmations and changes count from then on. */
  historySince: string | null;
  totals: {
    cropsLabelled: number;
    labelWrites: number;
    confirmations: number;
    activeLabellers: number;
    /** Writes that replaced different, non-empty labels. */
    changes: number;
  };
  days: { date: string; labeller: string; crops: number }[];
  labellers: LabellerActivity[];
  /** Everyone who ever labelled in the chain, by first activity: the order colours follow. */
  chainLabellers: { labeller: string; firstAt: string }[];
  stores: { storeId: number | null; storeName: string | null; crops: number; labels: LabelMix }[];
  batches: {
    batchId: string;
    name: string;
    kind: OosBatchKind;
    crops: number;
    labels: LabelMix;
    labellers: Record<string, number>;
  }[];
  kindTotals: { kind: OosBatchKind; batches: number; crops: number; labels: LabelMix }[];
  relabel: {
    /** The queue now, whatever the dates. */
    statusCounts: Record<RelabelStatus, number>;
    readyByReason: Record<RelabelReasonKey, number>;
    /** Resolutions in the range. */
    outcomeCounts: RelabelOutcomeCounts;
    outcomeByLabeller: { labeller: string; outcomes: RelabelOutcomeCounts }[];
    outcomeByReason: Record<RelabelReasonKey, RelabelOutcomeCounts>;
    outcomeByRole: Record<RelabelRoleKey, RelabelOutcomeCounts>;
    outcomeByDay: { date: string; outcomes: RelabelOutcomeCounts }[];
    /** Changed crops from one label set to another; null is copies that disagreed. */
    transitions: { fromLabels: OosCropLabel[] | null; toLabels: OosCropLabel[]; crops: number }[];
  };
}

/**
 * A chain's ready or done relabel candidates by priority, as batch crops: each the first copy, with
 * the copies' common labels (the first copy's when they disagree). `total` counts the matching
 * candidates before the limit.
 */
export interface RelabelQueue {
  chainId: number;
  chainName: string;
  status: RelabelQueueStatus;
  rule: RelabelRule | null;
  total: number;
  crops: OosBatchCrop[];
  scores: OosModelScores[];
}

/** A relabel write: the crop's new labels, with the copies' labels and the status the client saw. */
export interface RelabelWriteInput {
  cropId: string;
  labels: OosCropLabel[];
  expected: RelabelCopyLabels[];
  status: RelabelQueueStatus;
}

/**
 * What a relabel save did with one crop: an outcome, kept (a done crop given its labels again),
 * conflict (changed since read; `crop` is the server's row and nothing was written) or gone (no
 * longer queued; `crop` is null).
 */
export interface RelabelWriteResult {
  cropId: string;
  result: RelabelOutcome | 'kept' | 'conflict' | 'gone';
  crop: OosBatchCrop | null;
}

/** What sending crops to relabel did with them; test-store crops are refused. */
export interface RelabelSendCounts {
  saved: number;
  merged: number;
  reopened: number;
  test: number;
  unknown: number;
  /** Crops already queued for another chain, left alone. */
  otherChain: number;
  conflict: number;
}

// A backend from before crops took several labels sends only `label` and per-label counts; one
// from before imported batches sends no table labels, review flag or model scores.
type StoredBatchCrop = Omit<
  OosBatchCrop,
  'labels' | 'tableLabels' | 'needsReview' | 'relabel' | 'history'
> &
  Partial<Pick<OosBatchCrop, 'labels' | 'tableLabels' | 'needsReview' | 'relabel' | 'history'>>;
type StoredBatch = Omit<OosBatch, 'labelledCount' | 'assignees' | 'labelledBy'> &
  Partial<Pick<OosBatch, 'labelledCount' | 'assignees' | 'labelledBy'>>;

const withLabels = (crop: StoredBatchCrop): OosBatchCrop => ({
  ...crop,
  labels: crop.labels ?? (crop.label ? [crop.label] : []),
  tableLabels: crop.tableLabels ?? [],
  needsReview: crop.needsReview ?? false,
  relabel: crop.relabel ?? null,
  history: crop.history ?? [],
});

const withLabelledCount = (batch: StoredBatch): OosBatch => ({
  ...batch,
  assignees: batch.assignees ?? [],
  labelledBy: batch.labelledBy ?? {},
  labelledCount:
    batch.labelledCount ??
    Object.values(batch.labelCounts).reduce((sum, count) => sum + (count ?? 0), 0),
});

/** One label goes as `label`, which every backend reads; several, or none to clear, as `labels`. */
const labelWrite = ({ cropId, labels }: OosCropLabelInput) =>
  labels.length === 1 ? { cropId, label: labels[0] } : { cropId, labels };

/** A probability filter as a query value, e.g. `in:50-99,out:1-100`. */
const rangesParam = (filter: OosServedFilter[]) =>
  filter
    .map(({ state, minProbPct, maxProbPct }) => `${state}:${minProbPct}-${maxProbPct}`)
    .join(',');

const batchUrl = (batchId: string) => `${LURKER_API_URL}/batches/${encodeURIComponent(batchId)}`;

/** QA's query: snake_case on the wire, one `labeller` per email. */
function qaQuery(filter: QaFilter, extra: Record<string, string | null | undefined> = {}): string {
  const query = new URLSearchParams({ chain_id: String(filter.chainId) });
  if (filter.firstDate) query.set('first_date', filter.firstDate);
  if (filter.lastDate) query.set('last_date', filter.lastDate);
  for (const labeller of filter.labellers) query.append('labeller', labeller);
  if (filter.exact) query.set('exact', 'true');
  if (filter.unsureWrong) query.set('unsure_wrong', 'true');
  for (const [key, value] of Object.entries(extra)) if (value) query.set(key, value);
  return query.toString();
}
const workBatchUrl = (batchId: string) =>
  `${LURKER_API_URL}/work/batches/${encodeURIComponent(batchId)}`;

/** The most crop ids one Send to relabel request may carry (the server's MAX_MANUAL_CROPS). */
export const MAX_RELABEL_SEND = 500;

/** A Send to relabel part that failed after earlier parts went through; `sent` adds those up. */
export class RelabelSendError extends Error {
  sent: RelabelSendCounts;

  constructor(message: string, sent: RelabelSendCounts) {
    super(message);
    this.name = 'RelabelSendError';
    this.sent = sent;
  }
}

/** The most crops one relabel save may carry (the server's MAX_RELABEL_WRITES). */
export const MAX_RELABEL_WRITES = 200;

const cameraDayUrl = (cameraDayId: string) =>
  `${LURKER_API_URL}/camera-days/${encodeURIComponent(cameraDayId)}`;

export const LurkerApiService = {
  async fetchStrategies(token: string): Promise<OosStrategy[]> {
    const response = await forgeRequest<{ strategies: OosStrategy[] }>(
      token,
      `${LURKER_API_URL}/strategies`,
      'Failed to load strategies'
    );
    return response.strategies;
  },

  createStrategy(token: string, fields: OosStrategyFields): Promise<OosStrategy> {
    return forgeRequest<OosStrategy>(
      token,
      `${LURKER_API_URL}/strategies`,
      'Failed to create strategy',
      {
        method: 'POST',
        body: fields,
      }
    );
  },

  updateStrategy(token: string, id: string, fields: OosStrategyFields): Promise<OosStrategy> {
    return forgeRequest<OosStrategy>(
      token,
      `${LURKER_API_URL}/strategies/${encodeURIComponent(id)}`,
      'Failed to save strategy',
      { method: 'PUT', body: fields }
    );
  },

  deleteStrategy(token: string, id: string): Promise<void> {
    return forgeVoidRequest(
      token,
      `${LURKER_API_URL}/strategies/${encodeURIComponent(id)}`,
      'Failed to delete strategy',
      { method: 'DELETE' }
    );
  },

  /**
   * The ended camera-days with results of every (or any) chosen source. Only PR shadow alone lists
   * every store; anything else lists the chosen stores.
   */
  async fetchCandidates(
    token: string,
    range: OosDateRange,
    sources: OosSource[],
    storeIds: number[] = [],
    match: OosCandidateMatch = 'all'
  ): Promise<OosCandidate[]> {
    const query = new URLSearchParams(rangeQuery(range));
    for (const source of sources) query.append('sources', source);
    for (const storeId of storeIds) query.append('store_ids', String(storeId));
    query.set('match', match);
    const response = await forgeRequest<{ candidates: OosCandidate[] }>(
      token,
      `${LURKER_API_URL}/candidates?${query}`,
      'Failed to load camera-days to snapshot'
    );
    return response.candidates;
  },

  /** The snapshotted camera-days, newest first; without a range, of every date. */
  async fetchCameraDays(
    token: string,
    range?: OosDateRange,
    filter?: OosCameraDayFilter
  ): Promise<OosCameraDaySummary[]> {
    const response = await forgeRequest<{ cameraDays: OosCameraDaySummary[] }>(
      token,
      listUrl('camera-days', range, filter),
      'Failed to load camera-days'
    );
    return response.cameraDays;
  },

  /** Every tag on the camera-days the user can see, by name. */
  async fetchCameraDayTags(token: string): Promise<OosTagCount[]> {
    const response = await forgeRequest<{ tags: OosTagCount[] }>(
      token,
      `${LURKER_API_URL}/camera-day-tags`,
      'Failed to load tags'
    );
    return response.tags;
  },

  /** Adds and removes tags on every camera-day; returns their tags after the change. */
  async tagCameraDays(
    token: string,
    ids: string[],
    add: string[],
    remove: string[]
  ): Promise<Pick<OosCameraDaySummary, 'id' | 'tags'>[]> {
    const response = await forgeRequest<{ cameraDays: Pick<OosCameraDaySummary, 'id' | 'tags'>[] }>(
      token,
      `${LURKER_API_URL}/camera-days/tags`,
      'Failed to tag camera-days',
      { method: 'POST', body: { ids, add, remove } }
    );
    return response.cameraDays;
  },

  /** Deletes the camera-days for good, with their results, labels and scores. */
  deleteCameraDays(token: string, ids: string[]): Promise<OosCameraDaysDeleted> {
    return forgeRequest<OosCameraDaysDeleted>(
      token,
      `${LURKER_API_URL}/camera-days/delete`,
      'Failed to delete camera-days',
      { method: 'POST', body: { ids } }
    );
  },

  snapshotCameraDay(token: string, cameraId: number, date: string): Promise<OosCameraDaySummary> {
    return forgeRequest<OosCameraDaySummary>(
      token,
      `${LURKER_API_URL}/camera-days`,
      'Failed to snapshot the camera-day',
      { method: 'POST', body: { cameraId, date } }
    );
  },

  fetchCameraDay(token: string, cameraDayId: string, signal?: AbortSignal): Promise<OosCameraDay> {
    return forgeRequest<OosCameraDay>(
      token,
      cameraDayUrl(cameraDayId),
      'Failed to load the camera-day',
      {
        signal,
      }
    );
  },

  async fetchItemImages(
    token: string,
    cameraDayId: string,
    signal?: AbortSignal
  ): Promise<OosItemImages[]> {
    const response = await forgeRequest<{ cameraDayId: string; items: OosItemImages[] }>(
      token,
      `${cameraDayUrl(cameraDayId)}/item-images`,
      'Failed to load item images',
      { signal }
    );
    return response.items;
  },

  /** The POG sections of the camera-day's items, read live, ordered by planogram mod. */
  async fetchPlanograms(
    token: string,
    cameraDayId: string,
    signal?: AbortSignal
  ): Promise<TracePog[]> {
    const response = await forgeRequest<{ cameraDayId: string; planograms: TracePog[] }>(
      token,
      `${cameraDayUrl(cameraDayId)}/planograms`,
      'Failed to load the planograms',
      { signal }
    );
    return response.planograms;
  },

  saveCellLabels(
    token: string,
    cameraDayId: string,
    labels: OosCellLabelInput[],
    baseRevision: number
  ): Promise<OosLabelsSaved> {
    return forgeRequest<OosLabelsSaved>(
      token,
      `${cameraDayUrl(cameraDayId)}/cell-labels`,
      'Failed to save labels',
      { method: 'PUT', body: { labels, baseRevision } }
    );
  },

  savePairingLabels(
    token: string,
    cameraDayId: string,
    labels: OosPairingLabelInput[],
    baseRevision: number
  ): Promise<OosLabelsSaved> {
    return forgeRequest<OosLabelsSaved>(
      token,
      `${cameraDayUrl(cameraDayId)}/pairing-labels`,
      'Failed to save pairing labels',
      { method: 'PUT', body: { labels, baseRevision } }
    );
  },

  saveScore(
    token: string,
    cameraDayId: string,
    body: { scoringVersion: number; snapshotAt: string; labelsRevision: number; score: OosScore }
  ): Promise<void> {
    return forgeVoidRequest(
      token,
      `${cameraDayUrl(cameraDayId)}/score`,
      'Failed to save the score',
      {
        method: 'PUT',
        body,
      }
    );
  },

  /** The stored scores of the snapshotted camera-days; without a range, of every date. */
  async fetchScores(
    token: string,
    range?: OosDateRange,
    filter?: OosCameraDayFilter
  ): Promise<OosCameraDayScore[]> {
    const response = await forgeRequest<{ scores: OosCameraDayScore[] }>(
      token,
      listUrl('scores', range, filter),
      'Failed to load scores'
    );
    return response.scores;
  },

  /** The error buckets: the defaults first, then the others oldest first. */
  async fetchErrorBuckets(token: string): Promise<OosErrorBucket[]> {
    const response = await forgeRequest<{ buckets: OosErrorBucket[] }>(
      token,
      `${LURKER_API_URL}/error-buckets`,
      'Failed to load the error buckets'
    );
    return response.buckets;
  },

  /** Adds a bucket; a 409 says one of the same name, ignoring case, exists. */
  createErrorBucket(token: string, name: string): Promise<OosErrorBucket> {
    return forgeRequest<OosErrorBucket>(
      token,
      `${LURKER_API_URL}/error-buckets`,
      'Failed to add the error bucket',
      { method: 'POST', body: { name } }
    );
  },

  /**
   * The bucket assignments of the given camera-days, or, without ids, of the camera-days a scores
   * read with the same range and filter lists.
   */
  async fetchBucketAssignments(
    token: string,
    { cameraDayIds, range, filter }: OosBucketAssignmentQuery = {}
  ): Promise<OosBucketAssignment[]> {
    // No ids would read every listed camera-day, so an empty list asks nothing.
    if (cameraDayIds && !cameraDayIds.length) return [];
    const url = cameraDayIds
      ? `${LURKER_API_URL}/error-bucket-assignments?${new URLSearchParams(
          cameraDayIds.map((id) => ['camera_day_ids', id])
        )}`
      : listUrl('error-bucket-assignments', range, filter);
    const response = await forgeRequest<{ assignments: OosBucketAssignment[] }>(
      token,
      url,
      'Failed to load the error buckets assigned'
    );
    return response.assignments;
  },

  /** Sets or clears assignments of one camera-day; returns all of its assignments after. */
  async saveBucketAssignments(
    token: string,
    cameraDayId: string,
    assignments: OosBucketAssignmentInput[]
  ): Promise<OosBucketAssignment[]> {
    const response = await forgeRequest<{ assignments: OosBucketAssignment[] }>(
      token,
      `${cameraDayUrl(cameraDayId)}/error-bucket-assignments`,
      'Failed to save the error bucket',
      { method: 'PUT', body: { assignments } }
    );
    return response.assignments;
  },

  fetchSettings(token: string): Promise<OosLurkerSettings> {
    return forgeRequest<OosLurkerSettings>(
      token,
      `${LURKER_API_URL}/settings`,
      'Failed to load the Lurker settings'
    );
  },

  /**
   * Saves the settings if they are still as loaded: `expectedUpdatedAt` is the `updatedAt` read,
   * null when nobody had saved. Otherwise it throws a ForgeApiError with status 409
   * (`isSettingsConflict`) and writes nothing.
   */
  saveSettings(
    token: string,
    excludedModPrefixes: string[],
    expectedUpdatedAt: string | null
  ): Promise<OosLurkerSettings> {
    return forgeRequest<OosLurkerSettings>(
      token,
      `${LURKER_API_URL}/settings`,
      'Failed to save the Lurker settings',
      { method: 'PUT', body: { excludedModPrefixes, expectedUpdatedAt } }
    );
  },

  /** The batches of the chains the user can see, newest first, of the kinds and chain asked. */
  async fetchBatches(
    token: string,
    { kinds, chainId }: OosBatchListFilter = {}
  ): Promise<OosBatch[]> {
    const query = new URLSearchParams();
    if (kinds?.length) query.set('kinds', kinds.join(','));
    if (chainId !== undefined) query.set('chain_id', String(chainId));
    const params = query.toString();
    const response = await forgeRequest<{ batches: StoredBatch[] }>(
      token,
      `${LURKER_API_URL}/batches${params ? `?${params}` : ''}`,
      'Failed to load batches'
    );
    return response.batches.map(withLabelledCount);
  },

  /** Draws the crops at random and freezes them as a new batch. */
  createBatch(token: string, body: OosBatchRequest): Promise<OosBatch> {
    return forgeRequest<OosBatch>(
      token,
      `${LURKER_API_URL}/batches`,
      'Failed to create the batch',
      {
        method: 'POST',
        body,
      }
    );
  },

  /** How many crops the stores hold for the kind and dates, before a batch draws from them. */
  fetchBatchAvailability(
    token: string,
    request: OosBatchAvailabilityRequest,
    signal?: AbortSignal
  ): Promise<OosBatchAvailability> {
    // Store and chain ids are integers, so the comma-joined lists need no escaping.
    const scope =
      'chainId' in request
        ? `chain_id=${request.chainId}${
            request.excludedStoreIds.length
              ? `&excluded_store_ids=${request.excludedStoreIds.join(',')}`
              : ''
          }`
        : `store_ids=${request.storeIds.join(',')}`;
    const filters = new URLSearchParams();
    if (request.source === 'bigquery') {
      if (request.servedFilter?.length)
        filters.set('served_filter', rangesParam(request.servedFilter));
      if (request.classFilter?.length)
        filters.set('class_filter', rangesParam(request.classFilter));
      if (request.modPrefixes?.length) filters.set('mod_prefixes', request.modPrefixes.join(','));
    }
    const filterQuery = filters.toString();
    const query = `kind=${encodeURIComponent(request.kind)}&source=${request.source}&${scope}&${rangeQuery(
      request
    )}${filterQuery ? `&${filterQuery}` : ''}`;
    return forgeRequest(
      token,
      `${LURKER_API_URL}/batches/availability?${query}`,
      'Failed to count the available crops',
      { signal }
    );
  },

  /** Crops already in batches and how many are labelled, per store and kind. */
  async fetchBatchCollected(token: string, signal?: AbortSignal): Promise<OosBatchCollected[]> {
    const response = await forgeRequest<{ stores: OosBatchCollected[] }>(
      token,
      `${LURKER_API_URL}/batches/collected`,
      'Failed to count the collected crops',
      { signal }
    );
    return response.stores;
  },

  async fetchBatch(
    token: string,
    batchId: string,
    signal?: AbortSignal
  ): Promise<{
    batch: OosBatch;
    crops: OosBatchCrop[];
    scores: OosModelScores[];
    agreement: OosVotePair[];
  }> {
    const response = await forgeRequest<{
      batch: StoredBatch;
      crops: StoredBatchCrop[];
      scores?: OosModelScores[];
      agreement?: OosVotePair[];
    }>(token, batchUrl(batchId), 'Failed to load the batch', { signal });
    return {
      batch: withLabelledCount(response.batch),
      crops: response.crops.map(withLabels),
      scores: response.scores ?? [],
      agreement: response.agreement ?? [],
    };
  },

  /** The batches of the chain holding a crop, its label history and the training runs that used it. */
  fetchBatchCrop(
    token: string,
    batchId: string,
    cropId: string,
    signal?: AbortSignal
  ): Promise<OosCropDetail> {
    return forgeRequest<OosCropDetail>(
      token,
      `${batchUrl(batchId)}/crops/${encodeURIComponent(cropId)}`,
      'Failed to load the crop',
      { signal }
    );
  },

  saveBatchLabels(
    token: string,
    batchId: string,
    labels: OosCropLabelInput[]
  ): Promise<{ saved: number }> {
    return forgeRequest(token, `${batchUrl(batchId)}/labels`, 'Failed to save labels', {
      method: 'PUT',
      body: { labels: labels.map(labelWrite) },
    });
  },

  /** Replaces the batch's assignees; empty unassigns it. */
  async setBatchAssignees(token: string, batchId: string, assignees: string[]): Promise<OosBatch> {
    const batch = await forgeRequest<StoredBatch>(
      token,
      `${batchUrl(batchId)}/assignees`,
      'Failed to assign the batch',
      { method: 'PUT', body: { assignees } }
    );
    return withLabelledCount(batch);
  },

  /**
   * Makes the batch a voting batch or changes its setting, re-evaluating every crop; `dryRun`
   * answers what it would do and writes nothing.
   */
  async setBatchVoting(
    token: string,
    batchId: string,
    voting: { votesNeeded: number; consensusRule: OosConsensusRule; dryRun: boolean }
  ): Promise<OosVotingChanged> {
    const response = await forgeRequest<Omit<OosVotingChanged, 'batch'> & { batch: StoredBatch }>(
      token,
      `${batchUrl(batchId)}/voting`,
      'Failed to change the voting',
      { method: 'PUT', body: voting }
    );
    return { ...response, batch: withLabelledCount(response.batch) };
  },

  /** Each chain's holdout split with its batches; Lurker owners only. */
  async fetchHoldoutSplits(token: string): Promise<OosHoldoutSplit[]> {
    const { splits } = await forgeRequest<{
      splits: (Omit<OosHoldoutSplit, 'batches'> & { batches: StoredBatch[] })[];
    }>(token, `${LURKER_API_URL}/holdout-splits`, 'Failed to load the holdout splits');
    return splits.map((split) => ({ ...split, batches: split.batches.map(withLabelledCount) }));
  },

  /** What the signed-in user may do in Lurker. */
  fetchAccess(token: string, signal?: AbortSignal): Promise<LurkerAccess> {
    return forgeRequest(token, `${LURKER_API_URL}/access`, 'Failed to load your Lurker access', {
      signal,
    });
  },

  /** The caller's assigned batches, oldest first. */
  async fetchWork(token: string, signal?: AbortSignal): Promise<OosWorkBatch[]> {
    const response = await forgeRequest<{ batches: OosWorkBatch[] }>(
      token,
      `${LURKER_API_URL}/work`,
      'Failed to load your batches',
      { signal }
    );
    return response.batches;
  },

  /** The caller's oldest unfinished batch; null when every assigned batch is done. */
  async fetchNextWork(token: string, signal?: AbortSignal): Promise<OosWorkBatch | null> {
    const response = await forgeRequest<{ batch: OosWorkBatch | null }>(
      token,
      `${LURKER_API_URL}/work/next`,
      'Failed to find your next batch',
      { signal }
    );
    return response.batch;
  },

  /** A batch as the caller labels it: the crops no one else labelled, and their own. */
  fetchWorkBatch(
    token: string,
    batchId: string,
    signal?: AbortSignal
  ): Promise<{ batch: OosWorkBatch; crops: OosWorkCrop[] }> {
    return forgeRequest(token, workBatchUrl(batchId), 'Failed to load the batch', { signal });
  },

  /** Answers 409 when someone else labelled one of the crops, and writes nothing then. */
  saveWorkLabels(
    token: string,
    batchId: string,
    labels: OosCropLabelInput[]
  ): Promise<{ saved: number }> {
    return forgeRequest(token, `${workBatchUrl(batchId)}/labels`, 'Failed to save labels', {
      method: 'PUT',
      body: { labels: labels.map(labelWrite) },
    });
  },

  /** The crops labelled in, low, n/a or out, as a dl-oos2 table chunk. */
  downloadTrainingTable(token: string, batchId: string): Promise<{ blob: Blob; filename: string }> {
    return forgeBlobRequest(
      token,
      `${batchUrl(batchId)}/training-table`,
      'Failed to export the training table',
      `lurker_batch_${batchId}.csv`
    );
  },

  deleteBatch(token: string, batchId: string): Promise<void> {
    return forgeVoidRequest(token, batchUrl(batchId), 'Failed to delete the batch', {
      method: 'DELETE',
    });
  },

  /** Each labeller's accuracy on gold crops and the confusion matrix; Lurker owners only. */
  fetchQaSummary(token: string, filter: QaFilter, signal?: AbortSignal): Promise<QaSummary> {
    return forgeRequest(
      token,
      `${LURKER_API_URL}/qa/summary?${qaQuery(filter)}`,
      'Failed to load labeller QA',
      { signal }
    );
  },

  /** Wrong answers on gold crops, in one matrix cell when given; Lurker owners only. */
  fetchQaChecks(
    token: string,
    filter: QaChecksFilter,
    signal?: AbortSignal
  ): Promise<{ total: number; checks: QaCheck[] }> {
    const query = qaQuery(filter, { gold: filter.gold, answer: filter.answer, sort: filter.sort });
    return forgeRequest(token, `${LURKER_API_URL}/qa/checks?${query}`, 'Failed to load answers', {
      signal,
    });
  },

  fetchRelabelSummary(
    token: string,
    chainId: number,
    signal?: AbortSignal
  ): Promise<RelabelSummary> {
    return forgeRequest(
      token,
      `${LURKER_API_URL}/relabel/summary?chain_id=${chainId}`,
      'Failed to load the relabel queue',
      { signal }
    );
  },

  /** Labelling and relabel work; labellers go as repeated exact emails (ADR-0024). */
  fetchLabellingSummary(
    token: string,
    query: LabellingQuery,
    signal?: AbortSignal
  ): Promise<LabellingSummary> {
    const params = new URLSearchParams({
      chain_id: String(query.chainId),
      first_date: query.firstDate,
      last_date: query.lastDate,
    });
    for (const labeller of query.labellers) params.append('labeller', labeller);
    if (query.kind) params.set('kind', query.kind);
    if (query.batchId) params.set('batch_id', query.batchId);
    if (query.includeImported) params.set('include_imported', 'true');
    return forgeRequest(
      token,
      `${LURKER_API_URL}/labelling/summary?${params}`,
      'Failed to load the labelling summary',
      { signal }
    );
  },

  /**
   * The chain's ready or done relabel candidates by priority; `rule` keeps one reason's, and
   * `taskId` reads one relabel task's instead of the shared queue.
   */
  async fetchRelabelQueue(
    token: string,
    chainId: number,
    {
      status,
      rule,
      taskId,
    }: { status: RelabelQueueStatus; rule?: RelabelRule | null; taskId?: string | null },
    signal?: AbortSignal
  ): Promise<RelabelQueue> {
    const query = new URLSearchParams({ chain_id: String(chainId), status });
    if (rule) query.set('rule', rule);
    if (taskId) query.set('task_id', taskId);
    const response = await forgeRequest<
      Omit<RelabelQueue, 'crops' | 'scores'> & {
        crops: StoredBatchCrop[];
        scores?: OosModelScores[];
      }
    >(token, `${LURKER_API_URL}/relabel/queue?${query}`, 'Failed to load the relabel queue', {
      signal,
    });
    return { ...response, crops: response.crops.map(withLabels), scores: response.scores ?? [] };
  },

  /**
   * One relabel save of at most MAX_RELABEL_WRITES crops, each written to every batch copy; the
   * results come per crop, in the order sent.
   */
  async saveRelabelLabels(
    token: string,
    chainId: number,
    writes: RelabelWriteInput[],
    taskId: string | null = null
  ): Promise<RelabelWriteResult[]> {
    const response = await forgeRequest<{
      results: (Omit<RelabelWriteResult, 'crop'> & { crop: StoredBatchCrop | null })[];
    }>(token, `${LURKER_API_URL}/relabel/labels`, 'Failed to save labels', {
      method: 'PUT',
      body: taskId ? { chainId, taskId, labels: writes } : { chainId, labels: writes },
    });
    return response.results.map((entry) => ({
      ...entry,
      crop: entry.crop ? withLabels(entry.crop) : null,
    }));
  },

  /**
   * Relabel tasks newest first: a chain's (every one for an owner, else the caller's own), or
   * without a chain the caller's own across chains.
   */
  async fetchRelabelTasks(
    token: string,
    chainId: number | null,
    signal?: AbortSignal
  ): Promise<RelabelTask[]> {
    const query = chainId === null ? '' : `?chain_id=${chainId}`;
    const response = await forgeRequest<{ tasks: RelabelTask[] }>(
      token,
      `${LURKER_API_URL}/relabel/tasks${query}`,
      'Failed to load the relabel tasks',
      { signal }
    );
    return response.tasks;
  },

  /** Puts a voting batch's crops that need review (the given ones, or all) in a reviewer's task. */
  createRelabelTask(
    token: string,
    task: {
      chainId: number;
      batchId: string;
      cropIds: string[] | null;
      name: string;
      assignee: string;
    }
  ): Promise<RelabelTaskSaved> {
    return forgeRequest(token, `${LURKER_API_URL}/relabel/tasks`, 'Failed to create the task', {
      method: 'POST',
      body: task,
    });
  },

  reassignRelabelTask(token: string, taskId: string, assignee: string): Promise<RelabelTask> {
    return forgeRequest(
      token,
      `${LURKER_API_URL}/relabel/tasks/${encodeURIComponent(taskId)}`,
      'Failed to reassign the task',
      { method: 'PUT', body: { assignee } }
    );
  },

  /** Its crops leave it; those sent only because their votes disagreed leave the queue. */
  deleteRelabelTask(token: string, taskId: string): Promise<void> {
    return forgeVoidRequest(
      token,
      `${LURKER_API_URL}/relabel/tasks/${encodeURIComponent(taskId)}`,
      'Failed to delete the task',
      { method: 'DELETE' }
    );
  },

  /** Sends the crops in parts the server accepts, one after another, and adds up their counts. */
  async sendCropsToRelabel(
    token: string,
    chainId: number,
    cropIds: string[]
  ): Promise<RelabelSendCounts> {
    const total: RelabelSendCounts = {
      saved: 0,
      merged: 0,
      reopened: 0,
      test: 0,
      unknown: 0,
      otherChain: 0,
      conflict: 0,
    };
    for (let start = 0; start < cropIds.length; start += MAX_RELABEL_SEND) {
      let counts: RelabelSendCounts;
      try {
        counts = await forgeRequest<RelabelSendCounts>(
          token,
          `${LURKER_API_URL}/relabel/candidates`,
          'Failed to send the crops to relabel',
          {
            method: 'POST',
            body: { chainId, cropIds: cropIds.slice(start, start + MAX_RELABEL_SEND) },
          }
        );
      } catch (error) {
        // The parts already sent stay queued; the caller says what they did.
        if (start === 0) throw error;
        const message = error instanceof Error ? error.message : 'Sending failed';
        throw new RelabelSendError(
          `${message} after the first ${start} of ${cropIds.length} crops`,
          total
        );
      }
      for (const key of Object.keys(total) as (keyof RelabelSendCounts)[])
        total[key] += counts[key] ?? 0;
    }
    return total;
  },
};
